import { useNavigate, useParams } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { releaseSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { ContentBlock } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useStore } from '@/shared/store/store-context';

import { fromBlock, toBlockUpdate } from '../api/mappers';
import { BlockWarnings } from '../components/block-warnings';
import { BlockKindBadge, BlockScopeBadge, ContentStatusBadge } from '../components/content-badges';
import { DeleteBlockDialog } from '../components/delete-block-dialog';
import { BlockEditor } from '../components/editor/block-editor';
import { useBlock } from '../hooks/use-block';
import { useUpdateBlock } from '../hooks/use-block-mutations';

const STORE_EDIT_ROUTE = '/_authed/_active/s/$storeId/content/blocks/$blockId' as const;
const ORG_EDIT_ROUTE = '/_authed/_active/org/shared-blocks/$blockId' as const;

function DangerZone({ storeId, row }: { storeId: string | null; row: ContentBlock }) {
  const { t } = useTranslation('content');
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState<ContentBlock | null>(null);
  const back = () => {
    // The block is gone, so the dirty form has nothing left to save: let the navigation through.
    releaseSaveBar();
    void (storeId === null
      ? navigate({ to: '/org/shared-blocks' })
      : navigate({ to: '/s/$storeId/content/blocks', params: { storeId } }));
  };
  return (
    <FormSection title={t('dangerBlock.title')} className="border-destructive/30">
      <p className="text-muted-foreground text-sm">
        {t('dangerBlock.description', { count: row.sectionCount })}
      </p>
      <Button
        type="button"
        variant="outline"
        className="text-destructive hover:text-destructive w-full"
        onClick={() => setDeleting(row)}
      >
        <Trash2 aria-hidden />
        {t('actions.delete')}
      </Button>
      <DeleteBlockDialog
        storeId={storeId}
        row={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        onDeleted={back}
      />
    </FormSection>
  );
}

/**
 * Publication of a store rail's saved products, from the row's `product_unpublished` warnings
 * (`rail.source.products.<i>`, indexed in the saved order). A shared row yields nothing: the
 * org read has no store to check against.
 */
function publicationOf(row: ContentBlock): ReadonlyMap<string, boolean> | undefined {
  if (row.scope !== 'store' || row.rail?.source.type !== 'products') return undefined;
  const unpublished = new Set(
    row.warnings.flatMap((warning) => {
      const match = /^rail\.source\.products\.(\d+)$/.exec(warning.path);
      return warning.code === 'product_unpublished' && match ? [Number(match[1])] : [];
    }),
  );
  return new Map(
    row.rail.source.products.map((product, index) => [product.productId, !unpublished.has(index)]),
  );
}

function BlockEdit({ storeId, blockId }: { storeId: string | null; blockId: string }) {
  const { t } = useTranslation('content');
  const format = useFormat();
  // Prefetched by the route loader, so this resolves from cache.
  const { data: row } = useBlock(storeId, blockId);
  const update = useUpdateBlock(storeId);
  const publication = useMemo(() => (row ? publicationOf(row) : undefined), [row]);
  if (!row) return null;
  // A shared block opened from a store is read-only there; its own page edits it.
  const editable = storeId === null || row.scope === 'store';

  return (
    <PageContent>
      <PageHeader
        title={row.name}
        meta={
          <>
            <BlockKindBadge kind={row.kind} />
            <BlockScopeBadge scope={row.scope} />
            <ContentStatusBadge status={row.status} />
          </>
        }
        description={
          <span title={format.utc(row.updatedAt)}>
            {t('updatedAt', { when: format.relative(row.updatedAt) })}
            {' · '}
            <code>{row.key}</code>
            {' · '}
            {t('columns.sectionCount', { count: row.sectionCount })}
          </span>
        }
        backTo={
          storeId === null
            ? { to: '/org/shared-blocks' }
            : { to: '/s/$storeId/content/blocks', params: { storeId } }
        }
        backLabel={storeId === null ? t('blocks.sharedTitle') : t('blocks.title')}
      />
      <div className="flex flex-col gap-6">
        <BlockWarnings
          warnings={row.warnings}
          tour={storeId === null ? undefined : 'content.block-editor.warnings'}
        />
        <BlockEditor
          key={row.updatedAt}
          storeId={editable ? storeId : null}
          mode="edit"
          tour={storeId === null ? 'shared' : 'store'}
          defaultValues={fromBlock(row)}
          warnings={row.warnings}
          publication={publication}
          isSubmitting={update.isPending}
          onSubmit={(values) => update.mutateAsync({ id: row.id, body: toBlockUpdate(values) })}
          dangerZone={
            editable ? (
              <Can permission="content.write">
                <DangerZone storeId={storeId} row={row} />
              </Can>
            ) : null
          }
        />
      </div>
    </PageContent>
  );
}

function StoreBlockEdit() {
  const store = useStore();
  const { blockId } = useParams({ from: STORE_EDIT_ROUTE });
  return <BlockEdit storeId={store.id} blockId={blockId} />;
}

function SharedBlockEdit() {
  const { blockId } = useParams({ from: ORG_EDIT_ROUTE });
  return <BlockEdit storeId={null} blockId={blockId} />;
}

export function BlockEditPage() {
  return (
    <FeatureErrorBoundary name="content.blocks.edit">
      <StoreBlockEdit />
    </FeatureErrorBoundary>
  );
}

export function SharedBlockEditPage() {
  return (
    <FeatureErrorBoundary name="content.sharedBlocks.edit">
      <SharedBlockEdit />
    </FeatureErrorBoundary>
  );
}
